/**
 * GoogleStudio — the Google Wallet entry point (§3.3), a sibling of
 * AppleStudio.tsx. Honest parity with today's Google capability: there is NO
 * Google design backend (no PassDesignEditor, no visual preview), so this page
 * is TEMPLATES-ONLY: a template picker (Utazási biztosítás / Általános
 * biztosítás) → a recipient + per-template mint form → a result card with the
 * googleSaveUrl QR. One step, no StepChips, and NO style choice — the template
 * fixes the Google style (utazas → flight, altalanos → generic, see
 * STUDIO_GOOGLE_STYLE).
 *
 * The template lives in the URL (`/studio/google/:template`, slugs
 * `utazas`|`altalanos`). The segment is untrusted: an unknown template renders
 * the template picker. Legacy deep links redirect: a two-segment
 * `/studio/google/:template/:style` drops its style segment, and a
 * single-segment style link (`/studio/google/<style>`, pre-template era) maps
 * style → template (generic → altalanos, anything else known → utazas).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { mintPass } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { AssistanceMintForm } from '../components/studio/AssistanceMintForm';
import { MintSuccess } from '../components/studio/MintSuccess';
import { StyleGallery } from '../components/studio/StyleGallery';
import { TravelMintForm } from '../components/studio/TravelMintForm';
import {
  STUDIO_GOOGLE_STYLE,
  STUDIO_TEMPLATE_KINDS,
  studioTemplateCards,
  studioTemplateTitles,
  templateKindFromSlug,
  templateSlugForLegacyStyle,
  type StudioTemplateKind,
} from '../components/studio/templateCatalog';
import { useT } from '../i18n';
import { describeApiError } from '../lib/apiErrors';
import type { DemoPartner, DemoPass, MintPassRequest, PassTemplateKind } from '../types';
import { GOOGLE_STYLES, type GoogleStyle } from '../walletStyles';

export interface GoogleStudioPageProps {
  /** Injectable for tests; defaults to the authed partner's enabledTemplates. */
  enabledTemplates?: PassTemplateKind[];
}

/** Same honest no-access stub as AppleStudio — see TemplateGateStub there. */
function TemplateGateStub({ body }: { body: string }): JSX.Element {
  const t = useT();
  return (
    <div className="max-w-2xl mx-auto px-6 py-16 text-center space-y-3">
      <h1 className="text-xl font-semibold text-slate-900">{t('studio.google.title')}</h1>
      <p className="text-sm text-slate-600">
        {body} {t('studio.gate.contactPrefix')}{' '}
        <a href="mailto:hello@insurwiz.io" className="text-brand-700 hover:underline">
          hello@insurwiz.io
        </a>
        {t('studio.gate.contactSuffix')}
      </p>
    </div>
  );
}

export function GoogleStudioPage({ enabledTemplates }: GoogleStudioPageProps): JSX.Element {
  // Same unauthed-render guard as AppleStudio — component tests mount this page
  // with only <QueryClientProvider>+<MemoryRouter> and inject `enabledTemplates`.
  let partner: DemoPartner | null = null;
  try {
    partner = useAuth().partner;
  } catch {
    partner = null;
  }
  const t = useT();
  const allEnabled = enabledTemplates ?? partner?.enabledTemplates ?? ['travel'];
  const kinds = STUDIO_TEMPLATE_KINDS.filter((k) => allEnabled.includes(k));

  const navigate = useNavigate();
  // The template segment is untrusted — same validation as AppleStudio. There
  // is no style dimension any more: the template FIXES the Google style
  // (STUDIO_GOOGLE_STYLE); a legacy :style segment is redirected away below.
  const { template: templateParam, style: styleParam } = useParams<{
    template?: string;
    style?: string;
  }>();
  const templateKind = templateKindFromSlug(templateParam);

  // Legacy two-segment deep link (`/studio/google/:template/:style`, pre-
  // templates-only era): the style segment is IGNORED — canonicalize to the
  // single-segment URL (an invalid template falls back to the picker there).
  if (styleParam !== undefined) {
    return <Navigate to={`/studio/google/${templateParam ?? ''}`} replace />;
  }

  // Legacy single-segment style link (`/studio/google/<style>`, pre-template
  // era): map the style to the template that owns its layout now
  // (generic → altalanos, anything else known → utazas). Unknown segments
  // fall through to the template picker below.
  if (templateKind === null && templateParam !== undefined) {
    const legacySlug = templateSlugForLegacyStyle(
      templateParam,
      GOOGLE_STYLES.map((s) => s.value),
    );
    if (legacySlug !== null) {
      return <Navigate to={`/studio/google/${legacySlug}`} replace />;
    }
  }

  if (kinds.length === 0) {
    return <TemplateGateStub body={t('studio.gate.none')} />;
  }

  // Template picker — /studio/google, plus every unrecognized URL shape.
  if (templateKind === null) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t('studio.google.title')}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{t('studio.pickTemplate')}</p>
        </div>
        <StyleGallery
          options={studioTemplateCards(t).map((card) => {
            const enabled = kinds.includes(templateKindFromSlug(card.value) as StudioTemplateKind);
            return enabled
              ? card
              : { ...card, disabled: true, disabledHint: t('studio.notEnabledHint') };
          })}
          onPick={(slug) => navigate(`/studio/google/${slug}`)}
        />
      </div>
    );
  }

  if (!kinds.includes(templateKind)) {
    return (
      <TemplateGateStub
        body={t('studio.gate.template', { title: studioTemplateTitles(t)[templateKind] })}
      />
    );
  }

  // The template fixes the style — no gallery step between picker and form.
  const googleStyle: GoogleStyle = STUDIO_GOOGLE_STYLE[templateKind];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t('studio.google.title')}</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {templateKind === 'travel'
              ? t('studio.google.subtitle.travel')
              : t('studio.google.subtitle.assistance')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/studio/google')}
          className="text-sm text-brand-700 hover:underline"
        >
          {t('studio.changeTemplate')}
        </button>
      </div>
      <GoogleMintCard
        key={`${templateKind}:${googleStyle}`}
        templateKind={templateKind}
        googleStyle={googleStyle}
        defaultEmail={partner?.email ?? ''}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mint card — recipient + per-template data + Generate (Google-only mint with
// the template's fixed googleStyle) + the inline save-QR result. Both branches
// are the SHARED studio forms (TravelMintForm with its optional flight /
// AssistanceMintForm), which compose the shared passForms/mintPayloads
// fragments — so the surfaces stay in lock-step by construction. Only the
// transit-only testLat/testLng geofence is intentionally absent here (never
// applied to this path).
// ---------------------------------------------------------------------------

interface GoogleMintCardProps {
  templateKind: StudioTemplateKind;
  googleStyle: GoogleStyle;
  defaultEmail: string;
}

function GoogleMintCard({
  templateKind,
  googleStyle,
  defaultEmail,
}: GoogleMintCardProps): JSX.Element {
  const t = useT();
  const [result, setResult] = useState<DemoPass | null>(null);
  // Bumped by "Új pass" — remounts the form with fresh values.
  const [formKey, setFormKey] = useState(0);
  const queryClient = useQueryClient();
  const mint = useMutation({
    mutationFn: (req: MintPassRequest) => mintPass(req),
    onSuccess: (pass) => {
      // Same as every sibling mint surface — without it the Dashboard's
      // ['passes'] query (staleTime 10s / refetchInterval 30s) can miss the
      // studio-minted pass for up to ~30s.
      void queryClient.invalidateQueries({ queryKey: ['passes'] });
      setResult(pass);
    },
  });

  return (
    <div data-testid="studio-mint-form" className="card p-5 sm:p-6 space-y-4">
      {result ? (
        // The result REPLACES the form — no accidental second mint.
        <MintSuccess
          pass={result}
          onAnother={() => {
            setResult(null);
            mint.reset();
            setFormKey((k) => k + 1);
          }}
        />
      ) : (
        <>
          {templateKind === 'travel' ? (
            <TravelMintForm
              key={formKey}
              defaultEmail={defaultEmail}
              mint={mint}
              platformFields={{ offeredPlatforms: ['google'], googleStyle }}
            />
          ) : (
            <AssistanceMintForm
              key={formKey}
              defaultEmail={defaultEmail}
              mint={mint}
              platformFields={{ offeredPlatforms: ['google'], googleStyle }}
            />
          )}
          {mint.error ? (
            <div className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800">
              {describeApiError(mint.error, t)}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
